// Copyright (c) 2022 by Move Closer

import { ProductData } from '../../../../contexts/products'

import { toImageProps } from '../../../shared/support'

import { CartItemProps } from './CartItem.contracts'
import { AllowedImageRatio, CartItemData } from '../../../../contexts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export enum CartItemAttributes {
  Size = 'size',
  Color = 'color'
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const translateToCartItem = (
  product: ProductData,
  formatters: ('noQuantity' | 'isDisabled')[],
  shouldDisplayNetPrice: boolean
): CartItemProps => {
  if (typeof product !== 'object' || product === null) {
    return {
      description: '---',
      isDisabled: true,
      price: 0.00,
      properties: [],
      thumbnail: { alt: 'Unknown', src: '' },
      title: 'Unknown product'
    }
  }

  const products = []
  for (const value of Object.values(product.variants)) {
    products.push(value)
  }
  const _product = products[0]

  const attrs: string[] = []

  if (Object.values(_product.attributes).length > 0) {
    for (const [key, value] of Object.entries(_product.attributes)) {
      if (typeof value === 'string' && value.trim().length < 1) {
        continue
      }

      if (Object.values(CartItemAttributes).includes(key as CartItemAttributes)) {
        if (Array.isArray(value) && value.length) {
          attrs.push(...[...value.map((v) => v.toString())])
        } else {
          attrs.push(value.toString())
        }
      }
    }
  }

  return {
    categoryUrl: product.category?.url,
    thumbnail: Array.isArray(_product.images) && _product.images.length > 0 ? toImageProps(_product.images[0], AllowedImageRatio.Square) : { src: '', alt: '' },
    title: _product.attributes.productLine?.toString() ?? '',
    properties: attrs,
    price: _product.price.regularPrice,
    description: _product.name,
    quantity: formatters.includes('noQuantity') ? undefined : _product.sellableQuantity,
    isDisabled: formatters.includes('isDisabled'),
    promotionPrice: shouldDisplayNetPrice
      ? (_product.price.finalPriceNet === _product.price.regularPriceNet ? undefined : _product.price.finalPriceNet)
      : (_product.price.finalPrice === _product.price.regularPrice ? undefined : _product.price.finalPrice),
    previousPrice: _product.price.previousPrice,
    urlPath: product.urlPath
  }
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const translateToCartItemEnhanced = (
  product: ProductData,
  formatters: ('noQuantity' | 'isDisabled')[],
  shouldDisplayNetPrice: boolean,
  useSellableQuantity: boolean = true,
  item?: CartItemData
): CartItemProps => {
  if (typeof product !== 'object' || product === null) {
    return {
      description: '---',
      isDisabled: true,
      price: 0.00,
      properties: [],
      thumbnail: { alt: 'Unknown', src: '' },
      title: 'Unknown product'
    }
  }

  const products = []
  for (const value of Object.values(product.variants)) {
    products.push(value)
  }
  const _product = products[0]

  const attrs: string[] = []

  if (Object.values(_product.attributes).length > 0) {
    for (const [key, value] of Object.entries(_product.attributes)) {
      if (typeof value === 'string' && value.trim().length < 1) {
        continue
      }

      if (Object.values(CartItemAttributes).includes(key as CartItemAttributes)) {
        if (Array.isArray(value) && value.length) {
          attrs.push(...[...value.map((v) => v.toString())])
        } else {
          attrs.push(value.toString())
        }
      }
    }
  }

  let attributes: string[] = []

  if (product.variantSelector) {
    const attributeKeys = Object.keys(product.variantSelector)

    attributes = attributeKeys.map(key => {
      if (!product.variantSelector) {
        return ''
      }

      const value = product.variantSelector[key].find(attr => attr.slug === _product.identifier[key])

      return value ? value.label : ''
    })
  }

  return {
    categoryUrl: product.category?.url,
    thumbnail: Array.isArray(_product.images) && _product.images.length > 0 ? toImageProps(_product.images[0], AllowedImageRatio.Original) : { src: '', alt: '' },
    title: _product.attributes.brand ? String(_product.attributes.brand) : '',
    properties: attrs,
    price: _product.price.regularPrice,
    priceNett: _product.price.regularPriceNet, // item ? item.prices.price.value : undefined,
    description: _product.name,
    enhancedDesc: `${_product.attributes.main_category} ${attributes.join(', ')}`,
    quantity: formatters.includes('noQuantity') ? undefined : (useSellableQuantity ? _product.sellableQuantity : 1),
    isDisabled: formatters.includes('isDisabled'),
    promotionPrice: shouldDisplayNetPrice
      ? (_product.price.finalPriceNet === _product.price.regularPriceNet ? undefined : _product.price.finalPriceNet)
      : (_product.price.finalPrice === _product.price.regularPrice ? undefined : _product.price.finalPrice),
    previousPrice: _product.price.previousPrice,
    urlPath: product.urlPath
  }
}
